import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, InputSignal, OnInit, Output, Signal, ViewChild, WritableSignal, computed, inject, input, output, signal } from '@angular/core';
import { AccountService, PaymentMethod } from '../../../shared/account.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { CommonModule, Location } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ArrayOptionsItem, PaymentDataPayload } from '../../../shared/types/account.types';
import { PaymentMethodService } from '../../../settings/billing/payment-method/payment-method.service';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { provideNativeDateAdapter, DateAdapter, MAT_DATE_LOCALE, MAT_DATE_FORMATS } from '@angular/material/core';
import { CUSTOM_DATE_FORMATS } from '../../../shared/constants/date-formtats';
import { MatStep, MatStepper, MatStepperModule } from '@angular/material/stepper';
import { tap } from 'rxjs';
import { OnlyNumberDirective } from '../../../shared/directives/only-number.directive';
import { SignalsStoreService } from '../../../shared/signals-store.service';
import { EmptyMessageComponent } from '../../../shared/empty-message/empty-message.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CollectJSValidationResult, initialCollectJSValidationResult, CollectJSFieldTypes } from '../../../settings/billing/payment-method/payment-method.types';
import { LoadingService } from '../../../shared/loading.service';
import { PendingMembershipService } from '../../../shared/pending-membership/pending-membership.service';
import {
  MEMBERSHIP_CONFIRMATION_ERROR_CODES,
  MembershipConfirmation,
  PendingMembershipCharge
} from '../../../shared/pending-membership/pending-membership.types';

@Component({
    selector: 'app-payment-method-signup',
    providers: [provideNativeDateAdapter(),
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS.MONTH_AND_YEAR },
        PaymentMethodService
    ],
    imports: [
        FormsModule,
        CommonModule,
        MatFormFieldModule,
        MatStepperModule,
        MatInputModule,
        MatDatepickerModule,
        MatIconModule,
        ReactiveFormsModule,
        MatSlideToggleModule,
        MatSelectModule,
        OnlyNumberDirective,
        MatCheckboxModule,
        EmptyMessageComponent
    ],
    templateUrl: './payment-method-signup.component.html',
    styleUrl: './payment-method-signup.component.scss'
})
export class PaymentMethodSignupComponent implements OnInit {

  private paymentMethodService = inject(PaymentMethodService);
  accountService = inject(AccountService);
  private signalsStoreService = inject(SignalsStoreService);
  #loadingService = inject(LoadingService);
  #pendingMembershipService = inject(PendingMembershipService);

  @Output() formValidityChanged = new EventEmitter<boolean>();

  @Input() stepper!: MatStepper;
  @Input() firstStep!: MatStep;

  in_selectedMembership: InputSignal<any> = input<any>();

  out_paymentMethod = output<PaymentMethod>();
  out_isValidFormPaymentMethod = output<boolean>();
  out_paymentMethodCreated = output<void>();
  isWaitingResponse: any = signal(false);
  onNextStep = output<void>();

  paymentMethod: WritableSignal<PaymentMethod> = signal({
    cardCode: '',
    firstName: '',
    lastName: '',
    cardNumber: '',
    email: '',
    expirationDate: '',
    address: undefined,
    city: undefined,
    state: undefined,
    zipCode: undefined
  })

  isValidFormPaymentMethod = computed(() => this.validatePaymentMethodForm());
  paymentMethodValidations = new Map<string, WritableSignal<{ valid: boolean, error: string, validations: string[], touched: boolean }>>();
  isCheckedBillingAddress: WritableSignal<boolean> = signal(false);
  isBillingAddressRequired: Signal<boolean> = computed(() => {
    const checkedBillingAddress = this.isCheckedBillingAddress();
    const selectedMembership = this.in_selectedMembership();

    if (checkedBillingAddress) return true;

    const membershipRequireAddress = selectedMembership?.configuration?.requireAddress ?? true

    // Setup billing address validator
    if (!membershipRequireAddress)
      this.setUpBillingPaymentValidations();

    return !membershipRequireAddress
  })
  billingAddress: any = {};

  date = new FormControl<any>({ value: '', disabled: false });

  states: WritableSignal<ArrayOptionsItem[]> = signal([]);

  disclaimer = "We will only charge your card if you are a CSA or VIP customer. Otherwise, we won't charge your card until your first order. However, we do need a card on file for you to be able to start shopping."

  //#region NMI - CollectJS config:
  // TODO: This is to force interaction with the app for NMI issue. It's necessary to look for a best way to handle this:
  @ViewChild('btnNMIChangesTrigger') btnNMIChangesTrigger!: ElementRef;

  #cdr = inject(ChangeDetectorRef);

  collectJS_ccnumber_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_ccexp_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)
  collectJS_cvv_validation: WritableSignal<CollectJSValidationResult> = signal(initialCollectJSValidationResult)

  collectJSResponse: WritableSignal<any> = signal(null);
  //#endregion

  isDisabledSaveChanges: WritableSignal<boolean> = signal(false);

  //#region Pending membership confirmation (DEV-11022):

  /** The membership saving a card is about to start, while it is still unconfirmed.
   *  Non-null is what swaps the continue button for the one that opens the modal. */
  pendingMembershipCharge: WritableSignal<PendingMembershipCharge | null> = signal(null);

  /** The option the customer picked, sent with the card so the backend charges it. */
  #membershipConfirmation: MembershipConfirmation | null = null;

  /** The button CollectJS tokenizes from. Kept in the DOM but hidden while the
   *  confirmation is pending, and clicked once the customer has chosen. */
  @ViewChild('nmiPayButtonRef') nmiPayButtonRef!: ElementRef<HTMLButtonElement>;

  //#endregion

  ngOnInit(): void {
    this.setUpFormValidations();
    this.#checkPendingMembershipCharge();
    this.accountService.mustFetchStates$.pipe(
      tap(value => {
        if (value) {
          this.getStates();
          this.paymentMethodService.setUpNMI();
        }
      })
    ).subscribe();

    if (this.signalsStoreService.hasSession()) {
      this.getStates();
      this.paymentMethodService.setUpNMI();
    }

    this.#subscribeToCollectJsValidationChanges();
    this.#subscribeToCollectJSResponse();
    this.#subscribeToCollectJSTimeout();
  }

  private validatePaymentMethodForm() {
    const formFilled = !!(
      this.paymentMethod()?.firstName &&
      this.paymentMethod()?.lastName &&
      (
        !this.isBillingAddressRequired() ||
        (
          this.isBillingAddressRequired() &&
          this.paymentMethod()?.address &&
          this.paymentMethod()?.city &&
          this.paymentMethod()?.state &&
          this.paymentMethod()?.zipCode
        )
      )
    );

    if (!formFilled) {
      this.out_isValidFormPaymentMethod.emit(false);
      return false;
    }

    let valid = true;
    const keys: string[] = Array.from(this.paymentMethodValidations.keys());
    for (const key of keys) {
      if (!this.paymentMethodValidations.get(key)?.()?.valid) {
        valid = false;
        break;
      }
    }
    this.out_isValidFormPaymentMethod.emit(valid);
    this.formValidityChanged.emit(valid);
    return valid;
  }

  formatCreditCardNumber(event: any) {
    let value = event.target.value;

    if (!value) return;

    let cleaned = value.replace(/\D/g, '');

    cleaned = cleaned.substring(0, 16);

    let formatted = cleaned.replace(/(.{4})/g, '$1 ').trim();

    event.target.value = formatted;
    this.updatePaymentMethod({ cardNumber: cleaned });
  }

  private setUpFormValidations() {
    const defaultValue = { valid: true, error: '', validations: ['required'], touched: false };
    this.paymentMethodValidations.set('firstName', signal(defaultValue));
    this.paymentMethodValidations.set('lastName', signal(defaultValue));
    this.paymentMethodValidations.set('cardCode', signal({ ...defaultValue, validations: [...defaultValue.validations, 'number', 'exactLength'] }));
    this.paymentMethodValidations.set('cardNumber', signal(defaultValue));
  }

  private setUpBillingPaymentValidations() {
    const defaultValue = {
      valid: true,
      error: '',
      validations: ['required'],
      touched: false
    };

    this.paymentMethodValidations.set('address', signal(defaultValue));
    this.paymentMethodValidations.set('city', signal(defaultValue));
    this.paymentMethodValidations.set('state', signal(defaultValue));
    this.paymentMethodValidations.set('zipCode', signal({ ...defaultValue, validations: [...defaultValue.validations, 'number', 'zipCodeExactLength'] }));
  }

  private tearDownPaymentMethodValidations() {

    const resetValue = {
      valid: true,
      error: '',
      validations: [],
      touched: false
    };

    this.paymentMethodValidations.set('address', signal(resetValue));
    this.paymentMethodValidations.set('city', signal(resetValue));
    this.paymentMethodValidations.set('state', signal(resetValue));
    this.paymentMethodValidations.set('zipCode', signal(resetValue));
  }

  validatePaymentMethodField(key: string, value: string | any) {
    if (typeof value !== 'string')
      value = value.target.value;
    const entry = this.paymentMethodValidations.get(key);
    if (!entry) return;
    const data = entry();
    for (const validation of entry().validations) {
      switch (validation) {
        case 'required':
          data.error = !value.trim() ? 'This field is required' : ''
          break;
        case 'zipCodeExactLength':
          const auxValue = value.toString();
          data.error = auxValue.length !== 5 ? `This field requires ${5} digits` : '';
          break;
        default:
          break;
      }
    }

    this.paymentMethodValidations.set(key, signal({ valid: !data.error, error: data.error, validations: data.validations, touched: true }));
  }


  updatePaymentMethod(newValue: any) {
    this.paymentMethod.update(value => ({ ...value, ...newValue }));
    this.out_paymentMethod.emit(this.paymentMethod());

    // Validate form fields:
    const key = Object.keys(newValue)[0];
    const value = newValue[key];
    this.validatePaymentMethodField(key, value);
  }

  billingAddressToggleChangeHandler(checked: any) {

    this.updateFormFields();

    if (checked) {
      this.getStates()
      this.setUpBillingPaymentValidations()
    } else {
      this.tearDownPaymentMethodValidations()
    }
  }

  private updateFormFields() {
    const updatedPaymentMethod = {
      ...this.paymentMethod(),
      address: undefined,
      city: undefined,
      state: undefined,
      zipCode: undefined,
      additionalAddress: undefined
    };
    this.paymentMethod.set(updatedPaymentMethod);
    this.out_paymentMethod.emit(this.paymentMethod());
  }

  private getStates() {
    if (!this.states().length) {
      this.paymentMethodService.getStates().subscribe(states => this.states.set(states));
    }
  }

  private validForm(): boolean {

    if (this.isValidFormPaymentMethod())
      return true;

    Object.entries(this.paymentMethod())
      .forEach(([key, value]) => this.validatePaymentMethodField(key, { target: { value: value ?? '' } }));

    return false;
  }

  addPaymentMethod() {
    if (!this.validForm())
      return;

    const payload = this.setUpPaymentMethodPayload();
    this.accountService
      .addPaymentMethod(payload)
      .subscribe({
        next: (res: any) => {
          this.#loadingService.close();
          this.isDisabledSaveChanges.set(false);

          // The backend refused to charge a membership the customer has not agreed
          // to. It answers with the current amounts, so the modal can be reopened
          // from them and the save retried (DEV-11022).
          if (res?.error && MEMBERSHIP_CONFIRMATION_ERROR_CODES.includes(res.code)) {
            this.#promptMembershipConfirmation(res.data);
            return;
          }

          if (!res.error) {
            this.out_paymentMethodCreated.emit();
            this.onNextStep.emit();
          }
          this.btnNMIChangesTrigger.nativeElement.click();
        }
      });
  }

  private setUpPaymentMethodPayload(): PaymentDataPayload {

    const values = this.paymentMethod();
    if (this.isBillingAddressRequired()) {
      this.billingAddress = {
        additional: values.additionalAddress || '',
        street: values.address || '',
        city: values.city || '',
        zip: values.zipCode || '',
        stateCode: values.state || ''
      }
    } else {
      this.billingAddress = {};
    }
    return {
      billingAddress: this.billingAddress,
      card: {
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        number: this.collectJSResponse().card,
        token: this.collectJSResponse().token,
        expiration: this.collectJSResponse().exp
      },
      ...(this.#membershipConfirmation ? { membershipConfirmation: this.#membershipConfirmation } : {})
    };
  }

  //#region Pending membership confirmation (DEV-11022):

  #checkPendingMembershipCharge() {
    if (!this.#pendingMembershipService.shouldCheckPendingCharge()) return;

    this.#pendingMembershipService.getPendingCharge()
      .subscribe(pendingCharge => this.pendingMembershipCharge.set(pendingCharge));
  }

  /** Continue button while a membership is waiting to be confirmed. */
  confirmMembershipBeforeSaving() {
    if (!this.validForm()) return;
    this.#promptMembershipConfirmation(this.pendingMembershipCharge());
  }

  #promptMembershipConfirmation(pendingCharge: PendingMembershipCharge | null) {
    if (!pendingCharge) return;

    this.#pendingMembershipService.openConfirmation(pendingCharge).subscribe(confirmation => {
      // "Not now": the card is not sent, nothing is charged and the signup stays
      // where it is.
      if (!confirmation) return;

      this.#membershipConfirmation = confirmation;
      this.pendingMembershipCharge.set(null);
      this.#cdr.detectChanges();
      // An NMI token is spent on every attempt, so the save has to start from the
      // pay button again to tokenize the card still on screen.
      this.nmiPayButtonRef?.nativeElement?.click();
    });
  }

  //#endregion

  //#region NMI - CollectJS methods:

  #subscribeToCollectJsValidationChanges() {
    this.paymentMethodService.collectJSValidation$.pipe(
      tap((result: CollectJSValidationResult) => {
        switch (result.type) {
          case CollectJSFieldTypes.ccnumber:
            this.collectJS_ccnumber_validation.set(result);
            break;
          case CollectJSFieldTypes.ccexp:
            this.collectJS_ccexp_validation.set(result);
            break;
          case CollectJSFieldTypes.cvv:
            this.collectJS_cvv_validation.set(result);
            break;

          default:
            break;
        }

        if (!result.valid) {
          this.#loadingService.close();
          this.isDisabledSaveChanges.set(false);
        }

        this.#cdr.detectChanges();
      })
    ).subscribe()
  }

  #subscribeToCollectJSResponse() {
    this.paymentMethodService.collectJSResponse$.pipe(
      tap((response: any) => {
        if (!response) return;
        this.collectJSResponse.set(response);
        this.#cdr.detectChanges();
        setTimeout(() => {
          this.addPaymentMethod();
        }, 1000);
      })
    ).subscribe()
  }

  #subscribeToCollectJSTimeout() {
    this.paymentMethodService.collectJSTimeout$.pipe(
      tap(() => {
        this.#loadingService.close();
        this.isDisabledSaveChanges.set(false);
      })
    )
  }

  //#endregion

  saveChangesHandler() {
    this.#loadingService.isShowGlobalLoading.set(true);
    this.isDisabledSaveChanges.set(true);
  }

}
